import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';

import { TranslationKeyMap } from '../../coaching-insight.i18n';
import { firstLoadCheat } from '../../first-load-cheat';
import {
    ColumnConfigItem,
    Icon,
    PersonOfInterest,
} from '../models/coaching.global.model';
import { FLOAMetric, FLOAPerson } from '../models/floa.model';
import { SortDirection } from '../models/sorting.model';
import { FrontlineOAService } from '../services/frontline-oa.service';

// maintain a list of Team Member Metrics
@Component({
    selector: 'cmps-frontline-oa',
    templateUrl: './frontline-oa.component.html',
    // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FrontlineOAComponent implements OnInit {
    @Input() selectedUser: PersonOfInterest;
    @Input() active: string[];
    @Input() floaData: FLOAPerson;
    @Input() periodTypeId: number;
    @Input() periodTypeDate: Date;

    @Output() metricsRendered: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    @Output() selectedUserChange: EventEmitter<PersonOfInterest> =
        new EventEmitter<PersonOfInterest>();

    @ViewChild('oatooltip') oatooltip: TemplateRef<any>;

    public columns: Observable<ColumnConfigItem[]>;
    public metriccolumns: Observable<ColumnConfigItem[]>;

    i18n = TranslationKeyMap;
    hasPendingData = false;

    tempFloaData: FLOAPerson; // will effectively be the floaData (the Top Level user)

    // oaSort: Observable<SortDirection>;
    // expectedSort: Observable<SortDirection>;

    public activetab = 'tab-performance';
    public activecolumns: Observable<number>;

    barcolours = {
        numeratorColour: 'green',
        denominatorColour: 'red',
    };

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _floaService: FrontlineOAService, // private readonly _cdr: ChangeDetectorRef,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    appendSubordinates(subordinates?: FLOAPerson[], parent?: FLOAPerson) {
        parent.subOrdinates = subordinates;
        if (!subordinates) {
            parent.isExpanded = false;
        } else {
            parent.isExpanded = true;
        }

        this.assignParentBack(parent);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    appendMetrics(metrics?: FLOAMetric[], parent?: FLOAPerson) {
        parent.metrics = metrics;
        if (!metrics) {
            parent.isExpandedMetrics = false;
        } else {
            parent.isExpandedMetrics = true;
        }

        this.assignParentBack(parent);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    assignParentBack(d: FLOAPerson) {
        if (this.tempFloaData.userId === d.userId) {
            this.tempFloaData = d;
        } else {
            searchAndReplace(this.tempFloaData, d);
        }

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        function searchAndReplace(o: FLOAPerson, p: FLOAPerson) {
            if (o.userId === p.userId) {
                o = { ...p };
                return o;
            } else {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (o.subOrdinates && o.subOrdinates.length) {
                    for (let x = 0; x < o.subOrdinates.length; x++) {
                        const y = searchAndReplace(o.subOrdinates[x], p);
                        if (y && y.userId === o.subOrdinates[x].userId) {
                            o.subOrdinates[x] = y;
                        }
                    }
                }
            }
        }

        this._floaService.setCurrentFloaState(this.tempFloaData);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getFloaData(level: number, data: any) {
        const rowData = { ...data };

        if (level !== 1) {
            rowData.maxUncoached = this.floaData.fundedUncoached;
            rowData.maxCoached = this.floaData.fundedCoached;
        }

        rowData.levelClass = `level${level}`;
        rowData.level = level;

        return rowData;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.columns = this._floaService.oacolumns;
        this.metriccolumns = this._floaService.metricscolumns;
        this.columns.pipe(first()).subscribe();

        this.tempFloaData = { ...this.floaData };

        firstLoadCheat(this._el.nativeElement);
    }

    sortTeamByScore(force?: SortDirection): SortDirection {
        return this._floaService.sortTeamBy(
            'oascore',
            force,
            this.tempFloaData,
        );
    }

    sortTeamByExpected(force?: SortDirection): SortDirection {
        return this._floaService.sortTeamBy(
            'expected',
            force,
            this.tempFloaData,
        );
    }

    toggle(event, icon: Icon, columns: ColumnConfigItem[]): void {
        let newstate = null;

        if (icon.action) {
            newstate = this[icon.action](null, icon.actionParams);
        }

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (icon && icon.state !== undefined) {
            columns.forEach((g) => {
                if (g.icon) {
                    g.icon.forEach((f) => {
                        if (
                            newstate === SortDirection.DEFAULT &&
                            f.defaultState != null
                        ) {
                            f.counter = f.defaultState;
                        } else {
                            f.counter = SortDirection.DEFAULT;
                        }
                    });
                }
            });
            icon.state = !icon.state;
            icon.counter = newstate;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    checkIfAllCollapsed() {
        const floa = this.floaData;

        if (!floa.isExpanded && !floa.subOrdinates) {
            this.sortTeamByExpected(SortDirection.DEFAULT); // sort default
            this.sortTeamByScore(SortDirection.ASC);

            const cols = this._floaService.getColumns();
            // reset all column's sort
            cols.forEach((g) => {
                if (g.icon) {
                    g.icon.forEach((f) => {
                        if (f.defaultState != null) {
                            f.counter = f.defaultState;
                        } else {
                            f.counter = SortDirection.DEFAULT;
                        }
                    });
                }
            });
        }
    }
}
